/**
 * Data Flow Manager API Client
 */

import type {
  Flow,
  FlowTemplate,
  FlowExecution,
  FlowMetrics,
  ApiIngestPipeline,
  ExternalApi,
  SystemFlow,
  DataflowPipeline,
  TelemetryActivity,
  OpsStatus,
  ConnectionHealthResponse,
} from './types';
import { cpFetch } from '@/lib/cpClient';

async function df<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  // Orchestrator proxy is mounted at /cp/dataflow/* and cpFetch auto-injects:
  // - Authorization (if not expired)
  // - x-tenant-id
  // - x-app-id
  return (await cpFetch(`/dataflow${path.startsWith('/') ? path : `/${path}`}`, init)) as T;
}

/**
 * List flows
 */
export async function listFlows(params?: {
  status?: string;
  limit?: number;
  offset?: number;
  scope?: string;
}): Promise<{ flows: Flow[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  if (params?.scope) qs.set('scope', params.scope);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return await df(`/flows${suffix}`);
}

/**
 * Get flow by ID
 */
export async function getFlow(flowId: string): Promise<Flow> {
  const response = await df<{ success: boolean; flow: Flow }>(`/flows/${encodeURIComponent(flowId)}`);
  return response.flow;
}

/**
 * Create new flow
 */
export async function createFlow(flow: Partial<Flow>): Promise<Flow> {
  const response = await df<{ success: boolean; flow: Flow }>(`/flows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(flow),
  });
  return response.flow;
}

/**
 * Update flow
 */
export async function updateFlow(flowId: string, updates: Partial<Flow>): Promise<Flow> {
  const response = await df<{ success: boolean; flow: Flow }>(`/flows/${encodeURIComponent(flowId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return response.flow;
}

/**
 * Delete flow
 */
export async function deleteFlow(flowId: string): Promise<void> {
  await df(`/flows/${encodeURIComponent(flowId)}`, { method: 'DELETE' });
}

/**
 * Duplicate flow
 */
export async function duplicateFlow(flowId: string): Promise<Flow> {
  const response = await df<{ success: boolean; flow: Flow }>(`/flows/${encodeURIComponent(flowId)}/duplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return response.flow;
}

/**
 * Execute flow
 */
export async function executeFlow(flowId: string, inputData?: Record<string, any>): Promise<{ executionId: string }> {
  return await df(`/execute/${encodeURIComponent(flowId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: inputData || {} }),
  });
}

/**
 * Get execution status
 */
export async function getExecutionStatus(executionId: string): Promise<FlowExecution> {
  const response = await df<{ success: boolean; execution: FlowExecution }>(
    `/execute/status/${encodeURIComponent(executionId)}`
  );
  return response.execution;
}

/**
 * Get execution event history
 */
export async function getExecutionEvents(executionId: string): Promise<any[]> {
  const response = await df<{ success: boolean; events: any[] }>(
    `/execute/events/${encodeURIComponent(executionId)}`
  );
  return response.events || [];
}

/**
 * Get execution history
 */
export async function getExecutionHistory(flowId: string, params?: {
  limit?: number;
  offset?: number;
  status?: string;
}): Promise<{ executions: FlowExecution[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return await df(`/execute/${encodeURIComponent(flowId)}/history${suffix}`);
}

/**
 * List templates
 */
export async function listTemplates(): Promise<FlowTemplate[]> {
  const response = await df<{ success: boolean; templates: FlowTemplate[] }>('/templates');
  return response.templates;
}

/**
 * Get template
 */
export async function getTemplate(templateId: string): Promise<FlowTemplate> {
  const response = await df<{ success: boolean; template: FlowTemplate }>(`/templates/${encodeURIComponent(templateId)}`);
  return response.template;
}

/**
 * Instantiate template
 */
export async function instantiateTemplate(
  templateId: string,
  customization?: Record<string, any>
): Promise<Flow> {
  const response = await df<{ success: boolean; flow: Flow }>(`/templates/${encodeURIComponent(templateId)}/instantiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(customization || {}),
  });
  return response.flow;
}

/**
 * Get flow metrics
 */
export async function getFlowMetrics(flowId: string, timeRange?: string): Promise<FlowMetrics> {
  const qs = new URLSearchParams();
  if (timeRange) qs.set('timeRange', timeRange);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await df<{ success: boolean; metrics: FlowMetrics }>(
    `/monitoring/flows/${encodeURIComponent(flowId)}/metrics${suffix}`
  );
  return response.metrics;
}

/**
 * Get overall stats
 */
export async function getStats(timeRange?: string): Promise<any> {
  const qs = new URLSearchParams();
  if (timeRange) qs.set('timeRange', timeRange);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await df<{ success: boolean; stats: any }>(`/monitoring/stats${suffix}`);
  return response.stats;
}

/**
 * List API ingest pipelines (system flows)
 */
export async function listIngestPipelines(params?: {
  api_id?: string;
  endpoint_id?: string;
  enabled?: boolean;
}): Promise<ApiIngestPipeline[]> {
  const qs = new URLSearchParams();
  if (params?.api_id) qs.set('api_id', params.api_id);
  if (params?.endpoint_id) qs.set('endpoint_id', params.endpoint_id);
  if (params?.enabled != null) qs.set('enabled', String(params.enabled));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await cpFetch(`/api-ingest/api/pipelines${suffix}`);
  return response?.pipelines || [];
}

/**
 * List external APIs (to label ingest flows)
 */
export async function listExternalApis(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ExternalApi[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await cpFetch(`/api-ingest/api/external-apis${suffix}`);
  return response?.apis || [];
}

/**
 * Run API ingest pipeline
 */
export async function runIngestPipeline(pipelineId: string): Promise<any> {
  return await cpFetch(`/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
}

/**
 * Delete API ingest pipeline
 */
export async function deleteIngestPipeline(pipelineId: string): Promise<void> {
  await cpFetch(`/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}`, {
    method: 'DELETE'
  });
}

/**
 * List system flows (derived)
 */
export async function listSystemFlows(params?: {
  category?: string;
  deviceId?: string;
  sensorType?: string;
  flowType?: string;
  entityType?: string;
  entityId?: string;
  sourceSystem?: string;
  scope?: string;
  limit?: number;
}): Promise<SystemFlow[]> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set('category', params.category);
  if (params?.deviceId) qs.set('deviceId', params.deviceId);
  if (params?.sensorType) qs.set('sensorType', params.sensorType);
  if (params?.flowType) qs.set('flowType', params.flowType);
  if (params?.entityType) qs.set('entityType', params.entityType);
  if (params?.entityId) qs.set('entityId', params.entityId);
  if (params?.sourceSystem) qs.set('sourceSystem', params.sourceSystem);
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await df(`/system-flows${suffix}`);
  return response?.flows || [];
}

export async function getSystemFlowOverride(flowId: string): Promise<any | null> {
  const response = await df(`/system-flows/${encodeURIComponent(flowId)}/override`);
  return response?.override || null;
}

export async function createSystemFlowOverride(flowId: string, payload: Record<string, any>) {
  const response = await df(`/system-flows/${encodeURIComponent(flowId)}/override`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  });
  return response?.override;
}

export async function updateSystemFlowOverride(flowId: string, payload: Record<string, any>) {
  const response = await df(`/system-flows/${encodeURIComponent(flowId)}/override`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  });
  return response?.override;
}

export async function deleteSystemFlowOverride(flowId: string): Promise<void> {
  await df(`/system-flows/${encodeURIComponent(flowId)}/override`, { method: 'DELETE' });
}

/**
 * List dataflow pipelines
 */
export async function listDataflowPipelines(params?: {
  type?: string;
  scope?: string;
  limit?: number;
  offset?: number;
}): Promise<DataflowPipeline[]> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set('type', params.type);
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await df(`/pipelines${suffix}`);
  return response?.pipelines || [];
}

export async function createDataflowPipeline(payload: Partial<DataflowPipeline>): Promise<DataflowPipeline> {
  const response = await df(`/pipelines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return response?.pipeline;
}

export async function updateDataflowPipeline(id: string, payload: Partial<DataflowPipeline>): Promise<DataflowPipeline> {
  const response = await df(`/pipelines/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return response?.pipeline;
}

export async function deleteDataflowPipeline(id: string): Promise<void> {
  await df(`/pipelines/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * Get aggregated operational status of all data connections.
 */
export async function getOpsStatus(): Promise<OpsStatus> {
  return await df('/ops/status');
}

/**
 * Get per-entity connection health (Asset App uses this).
 */
export async function getConnectionHealth(params: {
  api_definition_id?: string;
  device_id?: string;
}): Promise<ConnectionHealthResponse> {
  const qs = new URLSearchParams();
  if (params.api_definition_id) qs.set('api_definition_id', params.api_definition_id);
  if (params.device_id) qs.set('device_id', params.device_id);
  return await df(`/ops/connection-health?${qs.toString()}`);
}

export async function getFlowActivity(params?: { deviceId?: string; limit?: number }): Promise<TelemetryActivity[]> {
  const qs = new URLSearchParams();
  if (params?.deviceId) qs.set('deviceId', params.deviceId);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await df(`/activity${suffix}`);
  return response?.items || [];
}

/**
 * Workspace APIs already used by API Ingest. Same read the current tab's sibling uses.
 */
export async function listWorkspaceApis(params?: {
  app_id?: string;
  window_minutes?: number;
  search?: string;
}): Promise<any[]> {
  const qs = new URLSearchParams();
  if (params?.app_id) qs.set('app_id', params.app_id);
  if (params?.window_minutes != null) qs.set('window_minutes', String(params.window_minutes));
  if (params?.search) qs.set('search', params.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await cpFetch(`/api-ingest/workspace/apis${suffix}`);
  return response?.apis || [];
}

/**
 * Ingest history ticks for the read-only side panel.
 */
export async function listIngestHistory(params?: {
  api_id?: string;
  app_id?: string;
  limit?: number;
}): Promise<any[]> {
  const qs = new URLSearchParams();
  if (params?.api_id) qs.set('api_id', params.api_id);
  if (params?.app_id) qs.set('app_id', params.app_id);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await cpFetch(`/api-ingest/api/ingest/history${suffix}`);
  return response?.history || [];
}

/**
 * One authored create-graph per selected app. Empty graph when none is stored.
 */
export async function getAuthoredGraph(): Promise<any> {
  const response = await df('/authored-graph');
  return response?.graph || { app_id: null, nodes: [], edges: [] };
}

export async function putAuthoredGraph(graph: {
  app_id: string;
  nodes: unknown[];
  edges: unknown[];
}): Promise<any> {
  const response = await df('/authored-graph', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(graph),
  });
  return response?.graph;
}

export async function listKongCatalogRoutes(): Promise<Array<{ name?: string; paths?: string[] }>> {
  const response = await cpFetch('/apis/catalog');
  return response?.routes || [];
}

export async function listMcpEndpoints(): Promise<Array<{
  app_id?: string;
  mcp_provisioned?: boolean;
  mcp_enabled?: boolean;
  mcp_endpoint?: string | null;
}>> {
  const response = await cpFetch('/api-expose/mcp/endpoints');
  return response?.endpoints || [];
}
